// Dashboard stats aggregation hook.
//
// Reads the most recent transactions + active events + active products
// and computes the metrics shown on the home dashboard. One query, one
// re-render — simpler than coordinating 4 separate useQuery calls in
// the page component, and the aggregate is cheap enough that we don't
// worry about partial staleness.
//
// Limitations (deliberate):
// - Scans the latest 100 transactions to derive "today" metrics.
//   For a typical event-day that's plenty. If you regularly blow past
//   100 txns/day, add a date-range query to lib/api/transactions.ts
//   and switch the hook to use it.

import { useQuery } from '@tanstack/react-query';
import { listRecentTransactions } from '@/lib/api/transactions';
import { listEvents } from '@/lib/api/events';

export interface TopProductToday {
  productId: string;
  name: string;
  qty: number;
  subtotal: number;
}

export interface DashboardStats {
  activeEventsCount: number;
  todayTransactions: number;
  todaySales: number;
  todayCancelledCount: number;
  pendingDeliveryCount: number;
  todayTopProducts: TopProductToday[];
}

export function useDashboardStats() {
  return useQuery({
    queryKey: ['dashboard', 'stats'] as const,
    queryFn: async (): Promise<DashboardStats> => {
      const [recentTxns, activeEvents] = await Promise.all([
        listRecentTransactions(100),
        listEvents({ status: 'active' }),
      ]);

      const now = new Date();
      const todayStart = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
      ).getTime();

      let todayTransactions = 0;
      let todaySales = 0;
      let todayCancelledCount = 0;
      let pendingDeliveryCount = 0;
      const productAgg = new Map<
        string,
        { name: string; qty: number; subtotal: number }
      >();

      for (const tx of recentTxns) {
        if (tx.paymentStatus === 'pending_delivery') {
          pendingDeliveryCount += 1;
        }
        const isToday = tx.createdAt >= todayStart;
        if (!isToday) continue;
        if (tx.paymentStatus === 'cancelled') {
          todayCancelledCount += 1;
          continue;
        }
        todayTransactions += 1;
        todaySales += tx.grandTotal;
        for (const item of tx.items) {
          const netQty = Math.max(0, item.qty - item.returnedQty);
          const subtotal = netQty * item.price;
          const existing = productAgg.get(item.productId);
          if (existing) {
            existing.qty += netQty;
            existing.subtotal += subtotal;
          } else {
            productAgg.set(item.productId, {
              name: item.name,
              qty: netQty,
              subtotal,
            });
          }
        }
      }

      const todayTopProducts: TopProductToday[] = Array.from(
        productAgg.entries(),
      )
        .map(([productId, v]) => ({ productId, ...v }))
        .sort((a, b) => b.subtotal - a.subtotal)
        .slice(0, 5);

      return {
        activeEventsCount: activeEvents.length,
        todayTransactions,
        todaySales,
        todayCancelledCount,
        pendingDeliveryCount,
        todayTopProducts,
      };
    },
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000, // background refresh once a minute
  });
}
